import React, { useEffect, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/EmptyState";
import { Search, Building2, Plus, Pencil, Power, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { money } from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";

const CURRENCIES = ["OMR", "AED", "USD", "EUR", "GBP", "SAR"];
const EMPTY = { name: "", code: "", contact_person: "", email: "", phone: "", whatsapp: "", country: "", currency: "OMR", payment_terms: "", warranty_contact: "", notes: "", sales_person: "", rma_person: "", sales_contact: "", rma_contact: "", sales_email: "", rma_email: "", province: "", ho_street: "", ho_area: "", ho_zip: "", sc_street: "", sc_area: "", sc_zip: "" };

function PerfBadge({ perf }) {
  if (!perf || perf.avg_rma_days == null) {
    return <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-500">{perf?.rating || "NO DATA"}</span>;
  }
  const colors = { green: "bg-emerald-50 text-emerald-700", lime: "bg-lime-50 text-lime-700", yellow: "bg-amber-50 text-amber-700", orange: "bg-orange-50 text-orange-700", red: "bg-rose-50 text-rose-700" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${colors[perf.rating_color] || "bg-slate-100 text-slate-600"}`}>
      {perf.rating} • {perf.avg_rma_days} Days
    </span>
  );
}

export default function Suppliers() {
  const { hasRole } = useAuth();
  const canManage = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER");
  const [suppliers, setSuppliers] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    const { data } = await api.get(`/suppliers?${params}`);
    setSuppliers(data);
  };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    try {
      const payload = { ...form };
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editing ? "Supplier updated" : "Supplier added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/suppliers/${s.id}/status?active=${!s.active}`);
      toast.success(s.active ? "Supplier deactivated" : "Supplier reactivated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewDetail = async (s) => {
    setDetail({ loading: true });
    try { const { data } = await api.get(`/suppliers/${s.id}`); setDetail(data); }
    catch { setDetail(null); }
  };

  return (
    <AdminLayout title="Suppliers">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="suppliers-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suppliers..." className="pl-9" />
        </div>
        {canManage && <Button onClick={openNew} data-testid="add-supplier-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Add Supplier</Button>}
      </div>

      {suppliers === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}</div>
      ) : suppliers.length === 0 ? (
        <EmptyState icon={Building2} title="No suppliers found" description="Try a different search." testId="suppliers-empty" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {suppliers.map((s) => (
            <div key={s.id} data-testid={`supplier-card-${s.id}`} className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <button onClick={() => viewDetail(s)} className="truncate text-left font-display font-semibold text-slate-900 hover:text-blue-600">{s.name}</button>
                  <div className="text-xs text-slate-400">{s.code} • {s.country || "—"}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {s.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <PerfBadge perf={s.performance} />
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                <div className="truncate">{s.sales_person || s.contact_person || "No contact"}</div>
                <div className="truncate text-xs text-slate-400">{s.sales_email || s.email || "—"}</div>
                <div className="text-xs text-slate-400">{s.currency} • {s.province || s.country || "—"}</div>
              </div>
              {canManage && (
                <div className="mt-4 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`} className="gap-1 text-xs"><Pencil className="h-3 w-3" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`toggle-supplier-${s.id}`} className="gap-1 text-xs"><Power className="h-3 w-3" /> {s.active ? "Deactivate" : "Activate"}</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Supplier" : "Add Supplier"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Supplier Name *" testId="sup-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="sup-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <F label="Sales Person" value={form.sales_person} onChange={(v) => setForm({ ...form, sales_person: v })} />
            <F label="RMA Person" value={form.rma_person} onChange={(v) => setForm({ ...form, rma_person: v })} />
            <F label="Sales Contact" value={form.sales_contact} onChange={(v) => setForm({ ...form, sales_contact: v })} />
            <F label="RMA Contact" value={form.rma_contact} onChange={(v) => setForm({ ...form, rma_contact: v })} />
            <F label="Sales Email" value={form.sales_email} onChange={(v) => setForm({ ...form, sales_email: v })} />
            <F label="RMA Email" value={form.rma_email} onChange={(v) => setForm({ ...form, rma_email: v })} />
            <F label="Province" value={form.province} onChange={(v) => setForm({ ...form, province: v })} />
            <F label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Head Office Address</Label>
              <div className="grid gap-3 sm:grid-cols-3">
                <F label="Street" value={form.ho_street} onChange={(v) => setForm({ ...form, ho_street: v })} />
                <F label="Area" value={form.ho_area} onChange={(v) => setForm({ ...form, ho_area: v })} />
                <F label="ZIP / PO" value={form.ho_zip} onChange={(v) => setForm({ ...form, ho_zip: v })} />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Service Centre Address</Label>
              <div className="grid gap-3 sm:grid-cols-3">
                <F label="Street" value={form.sc_street} onChange={(v) => setForm({ ...form, sc_street: v })} />
                <F label="Area" value={form.sc_area} onChange={(v) => setForm({ ...form, sc_area: v })} />
                <F label="ZIP / PO" value={form.sc_zip} onChange={(v) => setForm({ ...form, sc_zip: v })} />
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block text-sm font-medium">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-supplier-btn" className="bg-blue-600 hover:bg-blue-700">{editing ? "Save Changes" : "Add Supplier"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-2xl">
          {detail?.supplier && (
            <>
              <DialogHeader><DialogTitle>{detail.supplier.name}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Stat label="Open Claims" value={detail.stats.open_claims} />
                <Stat label="With Vendor" value={detail.stats.claims_with_vendor} />
                <Stat label="Overdue" value={detail.stats.overdue_claims} danger={detail.stats.overdue_claims > 0} />
                <Stat label="Total Claim Value" value={money(detail.stats.total_claim_value, detail.stats.currency)} />
                <Stat label="Credit Approved" value={money(detail.stats.credit_approved, detail.stats.currency)} />
                <Stat label="CN Received" value={money(detail.stats.cn_received, detail.stats.currency)} />
                <Stat label="Available Credit" value={money(detail.stats.available_credit, detail.stats.currency)} />
                <Stat label="Used Credit" value={money(detail.stats.used_credit, detail.stats.currency)} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function F({ label, testId, value, onChange }) {
  return (
    <div>
      <Label className="mb-1.5 block text-sm font-medium">{label}</Label>
      <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Stat({ label, value, danger }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`mt-0.5 font-display text-base font-bold ${danger ? "text-rose-600" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
